import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import {
  action,
  comparer,
  computed,
  IComputedValue,
  runInAction,
  untracked,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PopperLayer } from "react-atmosphere";
import {
  CUSTOM_RENDER_INPUT_LABEL,
  REACT_DOM_RENDER_INPUT_LABEL,
} from "../Constants";
import {
  ActionType,
  EvalPaneInputId,
  Pane,
  PaneInput,
  PaneInputGlobalKey,
  PaneType,
  PaneTypeNames,
  PaneViewMode,
  Position,
} from "../Types";
import { AnimatedPopout } from "../ui/AnimatedPopout";
import { ContextMenu, ContextMenuItem } from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { SelectItem, SelectRoot } from "../ui/Select";
import { TooltipClassName } from "../ui/Tooltip";
import {
  assert,
  generatePaneInputId,
  isModifierKeyDown,
  isPointInsideRect,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canvasAtom,
  canvasLayoutIdAtom,
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  selectedPaneIdsAtom,
  showPaneSettingsPaneIdAtom,
  transferPaneInputSourceBox,
} from "./CanvasState";
import { FocusPaneEmitter } from "./FocusPane";
import { dragValueAtom, handleNewPaneCreate } from "./NewPane";
import { isCustomLayoutId } from "./PaneLayout";
import {
  areEvalPaneInputIdsEqual,
  checkAndConfirmPushPaneInput,
  convertViewportPositionToCanvasOffset,
  getPaneIdFromPaneInput,
  getPaneInputAutoName,
  getPaneInputRect,
  getPaneOutputWirePosition,
  getWidthForMonospaceText,
  roundPosition,
  roundPositionToGrid,
  updatePaneInputWithSource,
  validatePaneInputName,
  validateVariableName,
} from "./SandboxUtils";

export function Wire({
  from,
  to,
  opacityClassName,
  disable,
  onClick,
  onContextMenu,
  className,
}: {
  from: Position;
  to: Position;
  opacityClassName?: string;
  disable?: boolean;
  onClick?: React.MouseEventHandler<unknown>;
  onContextMenu?: React.MouseEventHandler<unknown>;
  className?: string;
}) {
  const [x1, y1] = from;
  const [x2, y2] = to;
  return (
    <svg
      onClick={onClick}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onContextMenu={onContextMenu}
      className={classNames(
        "wire absolute top-0 left-0 w-1 h-1 text-neutral-800 overflow-visible hover:text-opacity-30 pointer-events-none transition",
        opacityClassName ?? "text-opacity-20",
        className
      )}
    >
      {disable !== true ? (
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          className="opacity-0 stroke-[8] stroke-current pointer-events-auto"
        />
      ) : null}
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        className="stroke-current stroke-1"
      />
    </svg>
  );
}

function PaneWireContextMenu({
  paneId,
  paneType,
  paneInputId,
  onClose,
}: {
  paneId: string;
  paneType: PaneType;
  paneInputId: string | ["render", "custom" | "react-dom"];
  onClose: () => void;
}) {
  return (
    <ContextMenu>
      <ContextMenuItem
        onClick={() => {
          if (Array.isArray(paneInputId)) {
            assert(paneInputId[0] === "render");
            canvasDispatch([
              ActionType.SetPaneRenderOutput,
              {
                paneId,
                renderOutput:
                  paneInputId[1] === "custom"
                    ? ["custom"]
                    : paneInputId[1] === "react-dom"
                    ? ["react"]
                    : undefined,
              },
            ]);
          } else if (paneType === PaneType.EvaluateTemplate) {
            canvasDispatch([
              ActionType.SetPaneTemplateInputSource,
              {
                paneId,
                paneInputId,
                source: undefined,
              },
            ]);
          } else if (paneType === PaneType.LLM) {
            canvasDispatch([
              ActionType.SetPaneLLMInputSource,
              {
                paneId,
                source: undefined,
              },
            ]);
          } else {
            canvasDispatch([
              ActionType.SetPaneInputSource,
              {
                paneId,
                paneInputId,
                source: undefined,
              },
            ]);
          }
          onClose();
        }}
        color="light-red"
      >
        <div className="flex items-center gap-1">
          <FeatherIcon icon="scissors" size="sm" />
          disconnect input
        </div>
      </ContextMenuItem>
      {paneType === PaneType.Evaluate && !Array.isArray(paneInputId) ? (
        <ContextMenuItem
          onClick={() => {
            canvasDispatch([
              ActionType.DeletePaneInput,
              {
                paneId,
                paneInputId,
              },
            ]);
            onClose();
          }}
          color="light-red"
        >
          <div className="flex items-center gap-1">
            <FeatherIcon icon="x" size="sm" />
            remove input
          </div>
        </ContextMenuItem>
      ) : null}
    </ContextMenu>
  );
}

const removeButtonClassName =
  "flex items-center text-red-400 hover:text-red-600 focus:text-red-600 transition";
function PaneInputPopup({
  paneId,
  paneType,
  paneInputs,
  paneInput,
  inputIndex,
  onRenameInput,
  onClose,
}: {
  paneId: string;
  paneType: PaneType;
  paneInputs: Array<PaneInput>;
  paneInput: PaneInput;
  inputIndex: number;
  onRenameInput:
    | ((paneInputId: string, newInputName: string) => Promise<void>)
    | undefined;
  onClose: () => void;
}) {
  const paneInputId = paneInput.id;
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const [value, setValue] = useState(paneInput.name ?? "");
  const inputPaneSource = paneInput.source;
  const autoInputPaneName = useMemo<string>(() => {
    const inputPane =
      inputPaneSource !== undefined
        ? untracked(() => canvasAtom.value).panes.find(
            (p) => p.id === inputPaneSource[0]
          )
        : undefined;
    return getPaneInputAutoName(
      paneInputs,
      inputPaneSource !== undefined ? inputPaneSource[1] : undefined,
      inputPane,
      paneInputId
    );
  }, [inputPaneSource]);

  async function updatePaneName(name: string, skipSemanticRename = false) {
    if (name === paneInput.name) {
      return false;
    }
    if (
      paneInputs.some(
        (paneInput) => paneInput.id !== paneInputId && paneInput.name === name
      )
    ) {
      return false;
    }
    if (!validateVariableName(name)) {
      return false;
    }
    if (onRenameInput !== undefined && !skipSemanticRename) {
      await onRenameInput(paneInput.id, name);
    }
    canvasDispatch([
      ActionType.SetPaneInputName,
      { paneId, paneInputId, name },
    ]);
    return true;
  }

  const isValid =
    paneInputs.every(
      (paneInput) => paneInput.id === paneInputId || paneInput.name !== value
    ) && validateVariableName(value);

  return (
    <div className="bg-neutral-0 p-2 rounded-md shadow-lg min-w-36">
      <div className="font-mono-natto text-neutral-400 mb-2">
        inputs[{inputIndex}]
      </div>
      <div className="">
        <input
          type="text"
          value={value}
          placeholder="input name"
          onChange={(e) => {
            setValue(e.target.value);
          }}
          onBlur={() => {
            updatePaneName(value);
          }}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              if (await updatePaneName(value, e.shiftKey)) {
                onClose();
              }
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
          className={classNames(
            "bg-neutral-100 font-mono-natto border border-neutral-300 rounded-sm px-1.5 py-1.5",
            !isValid ? "text-red-600" : undefined
          )}
          spellCheck="false"
          disabled={
            paneType === PaneType.EvaluateTemplate || paneType === PaneType.LLM
          }
          ref={inputRef}
        />
        {paneType === PaneType.Evaluate &&
        paneInput.name !== autoInputPaneName ? (
          <div>
            <button
              type="button"
              onClick={() => {
                setValue(autoInputPaneName);
                updatePaneName(autoInputPaneName);
                inputRef.current!.focus();
              }}
              disabled={value === autoInputPaneName}
              className={classNames(
                "font-mono-natto text-2xs",
                value !== autoInputPaneName
                  ? "text-neutral-500 hover:text-primary-500"
                  : "text-neutral-300"
              )}
            >
              set to {autoInputPaneName}
            </button>
          </div>
        ) : null}
      </div>
      <div>
        {inputPaneSource !== undefined ? (
          <>
            <div className="mt-2">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  // setTimeout to run after radix-ui popover focus management
                  setTimeout(() => {
                    FocusPaneEmitter.emit("focus", {
                      paneId: inputPaneSource[0],
                    });
                  }, 50);
                }}
                className="flex items-center text-neutral-400 hover:text-neutral-600 focus:text-neutral-600 transition-colors"
              >
                <FeatherIcon icon="arrow-left" size="sm" className="mr-1" />
                focus input pane
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  if (paneType === PaneType.EvaluateTemplate) {
                    canvasDispatch([
                      ActionType.SetPaneTemplateInputSource,
                      {
                        paneId,
                        paneInputId,
                        source: undefined,
                      },
                    ]);
                  } else if (paneType === PaneType.LLM) {
                    canvasDispatch([
                      ActionType.SetPaneLLMInputSource,
                      {
                        paneId,
                        source: undefined,
                      },
                    ]);
                  } else {
                    canvasDispatch([
                      ActionType.SetPaneInputSource,
                      {
                        paneId,
                        paneInputId,
                        source: undefined,
                      },
                    ]);
                  }
                  onClose();
                }}
                className={removeButtonClassName}
              >
                <FeatherIcon icon="scissors" size="sm" className="mr-1" />
                disconnect input
              </button>
            </div>
          </>
        ) : null}
        {paneType === PaneType.Evaluate ? (
          <div className="first:mt-2">
            <button
              type="button"
              onClick={() => {
                canvasDispatch([
                  ActionType.DeletePaneInput,
                  {
                    paneId,
                    paneInputId,
                  },
                ]);
                onClose();
              }}
              className={removeButtonClassName}
            >
              <FeatherIcon icon="x" size="sm" className="mr-1" />
              remove input
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

type SelectionState = "focused" | "selected";
function useIsPaneInputSelected(
  paneId: string,
  inputPaneId: string | undefined
): SelectionState | undefined {
  const isSelected =
    inputPaneId !== undefined &&
    computed(
      () =>
        selectedPaneIdsAtom.value.includes(paneId) ||
        selectedPaneIdsAtom.value.includes(inputPaneId)
    ).get();
  const isFocused = computed(
    () =>
      focusedPaneIdAtom.value === paneId ||
      (inputPaneId !== undefined && focusedPaneIdAtom.value === inputPaneId)
  ).get();
  const isDragTransformHover = computed(
    () =>
      dragValueAtom.value?.type === "reference" &&
      dragValueAtom.value.sourceInfo.type === "pane-output" &&
      dragValueAtom.value.sourceInfo.hoverPaneId === paneId
  ).get();
  if (isFocused || isDragTransformHover) {
    return "focused";
  }
  return isSelected ? "selected" : undefined;
}

const ROW_HEIGHT = 16;
export function useDragPaneOutputHandler(
  memoArg: any,
  last: boolean,
  down: boolean,
  xy: [number, number],
  inputPaneId: string,
  getInputPaneOutputIndex: () => number,
  duplicateInputName: string | undefined
) {
  const memo = memoArg ?? {};
  if (memo.inputPaneOutputIndex === undefined) {
    memo.inputPaneOutputIndex = getInputPaneOutputIndex();
  }
  const panes = canvasAtom.value.panes;
  const inputPaneOutputIndex: number = memo.inputPaneOutputIndex;
  const inputPane = panes.find((p) => p.id === inputPaneId);
  if (inputPane === undefined) {
    return memo;
  }
  if (last) {
    const { value } = dragValueAtom;
    if (value === undefined) {
      return;
    }
    assert(value.type === "reference");
    const { sourceInfo } = value;
    if (
      sourceInfo.type === "pane-output" &&
      sourceInfo.hoverPaneId !== undefined
    ) {
      const { hoverPaneId, hoverPaneInputKey } = sourceInfo;
      dragValueAtom.value = undefined;
      if (!checkAndConfirmPushPaneInput(panes, hoverPaneId, inputPaneId)) {
        return;
      }
      const hoverPane = panes.find((p) => p.id === hoverPaneId);
      if (hoverPane === undefined) {
        return;
      }
      if (hoverPaneInputKey !== undefined) {
        updatePaneInputWithSource(hoverPaneInputKey, [
          inputPaneId,
          inputPaneOutputIndex,
        ]);
      }
      if (hoverPane.type === PaneType.Evaluate) {
        const hoverPaneInputs = hoverPane.inputs;
        function getInputName(hoverPaneInputId?: EvalPaneInputId | undefined) {
          if (Array.isArray(hoverPaneInputId)) {
            return undefined;
          }
          return duplicateInputName !== undefined
            ? validatePaneInputName(
                duplicateInputName,
                hoverPaneInputs,
                hoverPaneInputId
              )
            : getPaneInputAutoName(
                hoverPaneInputs,
                inputPaneOutputIndex,
                inputPane,
                hoverPaneInputId
              );
        }
        if (hoverPaneInputKey !== undefined) {
          if (hoverPaneInputKey[0] === PaneType.Evaluate) {
            const hoverPaneInputId = hoverPaneInputKey[2];
            if (!Array.isArray(hoverPaneInputId)) {
              const hoverPaneInput = hoverPaneInputs.find(
                (paneInput) => paneInput.id === hoverPaneInputId
              )!;
              if (hoverPaneInput.name === undefined) {
                const autoName = getInputName(hoverPaneInputId);
                if (autoName !== undefined) {
                  canvasDispatch([
                    ActionType.SetPaneInputName,
                    {
                      paneId: hoverPaneId,
                      paneInputId: hoverPaneInputId,
                      name: autoName,
                    },
                  ]);
                }
              }
            }
          }
        } else {
          canvasDispatch([
            ActionType.PushPaneInput,
            {
              paneId: hoverPaneId,
              paneInput: {
                id: generatePaneInputId(),
                name: getInputName(),
                source: [inputPaneId, inputPaneOutputIndex],
              },
            },
          ]);
        }
      }
    } else {
      handleNewPaneCreate();
    }
    return;
  }

  if (down) {
    let hoverPane: Pane | undefined;
    let hoverPaneInputKey: PaneInputGlobalKey | undefined;
    panes
      .filter(
        (p) =>
          p.id !== inputPaneId &&
          (p.type === PaneType.Evaluate ||
            p.type === PaneType.EvaluateTemplate ||
            p.type === PaneType.LLM)
      )
      .sort((a, b) => b.z - a.z)
      .forEach((p) => {
        if (hoverPane !== undefined) {
          return;
        }
        const activePaneState = activePaneStates.get(p.id);
        if (
          activePaneState === undefined ||
          activePaneState.viewMode.get() === PaneViewMode.Hidden
        ) {
          return;
        }
        const paneRect = activePaneState.rect.get();
        if (p.type === PaneType.Evaluate && isPointInsideRect(xy, paneRect)) {
          hoverPane = p;
        } else if (
          "inputs" in p &&
          activePaneState.viewMode.get() === PaneViewMode.Normal
        ) {
          p.inputs.forEach((paneInput, inputIndex) => {
            if (
              isPointInsideRect(
                xy,
                getPaneInputRect(paneRect, paneInput?.name, inputIndex)
              )
            ) {
              hoverPane = p;
              hoverPaneInputKey = [PaneType.Evaluate, p.id, paneInput.id];
            }
          });
          if (hoverPane === undefined) {
            if (
              p.type === PaneType.Evaluate &&
              p.renderOutput?.[0] === "custom"
            ) {
              if (
                isPointInsideRect(
                  xy,
                  getPaneInputRect(
                    paneRect,
                    CUSTOM_RENDER_INPUT_LABEL,
                    p.inputs.length
                  )
                )
              ) {
                hoverPane = p;
                hoverPaneInputKey = [
                  PaneType.Evaluate,
                  p.id,
                  ["render", "custom"],
                ];
              }
            } else if (
              p.type === PaneType.Evaluate &&
              p.renderOutput?.[0] === "react"
            ) {
              if (
                isPointInsideRect(
                  xy,
                  getPaneInputRect(
                    paneRect,
                    REACT_DOM_RENDER_INPUT_LABEL,
                    p.inputs.length
                  )
                )
              ) {
                hoverPane = p;
                hoverPaneInputKey = [
                  PaneType.Evaluate,
                  p.id,
                  ["render", "react-dom"],
                ];
              }
            }
          }
        } else if (p.type === PaneType.EvaluateTemplate) {
          const activePaneState = activePaneStates.get(p.id)!;
          assert(activePaneState.type === PaneType.EvaluateTemplate);
          const paneInputs = activePaneState.paneInputs.value;
          if (paneInputs !== undefined) {
            paneInputs.forEach((paneInput, inputIndex) => {
              if (
                isPointInsideRect(
                  xy,
                  getPaneInputRect(paneRect, paneInput?.name, inputIndex)
                )
              ) {
                hoverPane = p;
                hoverPaneInputKey = [
                  PaneType.EvaluateTemplate,
                  p.id,
                  paneInput.id,
                ];
              }
            });
          }
        } else if (p.type === PaneType.LLM) {
          const activePaneState = activePaneStates.get(p.id)!;
          assert(activePaneState.type === PaneType.LLM);
          const paneInputs = activePaneState.paneInputsComputed.get();
          paneInputs.forEach((paneInput, inputIndex) => {
            if (
              isPointInsideRect(
                xy,
                getPaneInputRect(paneRect, paneInput?.name, inputIndex)
              )
            ) {
              hoverPane = p;
              hoverPaneInputKey = [PaneType.LLM, p.id, paneInput.id];
            }
          });
        }
      });
    dragValueAtom.value = {
      type: "reference",
      fromPaneId: inputPaneId,
      fromPaneOutputIndex: inputPaneOutputIndex,
      sourceInfo: {
        type: "pane-output",
        duplicateInputName,
        hoverPaneId: hoverPane?.id,
        hoverPaneInputKey,
      },
      position: roundPositionToGrid([xy[0], xy[1] - ROW_HEIGHT / 2], 1),
    };
  }
  return memo;
}

export type PopupPaneInputId = string | ["custom"] | ["react-dom"];

function usePaneInputBindDrag(
  paneInputSource: [paneId: string, outputIndex: number] | undefined,
  paneInputName: string | undefined
) {
  return useDrag(
    action<Handler<"drag">>(
      ({ first, last, down, xy, altKey, cancel, memo }) => {
        if (first) {
          if (!altKey) {
            cancel();
            return;
          }
        }

        if (paneInputSource !== undefined) {
          return useDragPaneOutputHandler(
            memo,
            last,
            down,
            xy,
            paneInputSource[0],
            () => paneInputSource[1],
            paneInputName
          );
        }
      }
    ),
    {
      filterTaps: true,
      transform: ([x, y]) =>
        untracked(() =>
          roundPosition(
            convertViewportPositionToCanvasOffset(
              [x, y],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            )
          )
        ),
    }
  );
}

export const PANE_INPUT_HEIGHT = 16;
const PANE_INPUT_POPUP_ALIGN_OFFSET = 39;
export const PaneInputNode = observer(
  ({
    inputIndex,
    paneId,
    paneType,
    paneInputs,
    paneInput,
    showPopupPaneInputId,
    setShowPopupPaneInputId,
    hideIfNotActive,
    onRenameInput,
    tooltipContent,
  }: {
    inputIndex: number;
    paneId: string;
    paneType: PaneType;
    paneInputs: Array<PaneInput>;
    paneInput: PaneInput;
    showPopupPaneInputId: PopupPaneInputId | undefined;
    setShowPopupPaneInputId: React.Dispatch<
      React.SetStateAction<PopupPaneInputId | undefined>
    >;
    hideIfNotActive: boolean;
    onRenameInput?: (
      paneInputId: string,
      newInputName: string
    ) => Promise<void>;
    tooltipContent?: React.ReactNode;
  }) => {
    const paneInputId = paneInput.id;
    const paneInputSource = paneInput.source;
    const selectionState = useIsPaneInputSelected(
      paneId,
      paneInputSource !== undefined ? paneInputSource[0] : undefined
    );
    const isPaneFocused = computed(
      () => focusedPaneIdAtom.value === paneId
    ).get();
    const isPopupShown = showPopupPaneInputId === paneInputId;
    const outputState = computed(() => {
      if (paneInputSource === undefined) {
        return undefined;
      }
      const [inputPaneId, outputIndex] = paneInputSource;
      const output = activePaneStates
        .get(inputPaneId)
        ?.outputBoxes[outputIndex].get();
      return output?.[0];
    }).get();

    const bindDrag = usePaneInputBindDrag(paneInputSource, paneInput.name);

    return (
      <div
        className="pane-input absolute font-mono-natto mr-[3px]"
        style={{
          right: "100%",
          top: `${inputIndex * PANE_INPUT_HEIGHT + 8}px`,
        }}
      >
        <TooltipPrimitive.Root delayDuration={500}>
          <PopoverPrimitive.Root
            open={isPopupShown}
            onOpenChange={(open) => {
              setShowPopupPaneInputId(open ? paneInputId : undefined);
            }}
            modal={true}
          >
            <PopoverPrimitive.Trigger asChild={true}>
              <TooltipPrimitive.Trigger asChild={true}>
                <button
                  {...bindDrag()}
                  onClick={(e) => {
                    if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                      e.preventDefault();
                      if (paneInputSource !== undefined) {
                        FocusPaneEmitter.emit("focus", {
                          paneId: paneInputSource[0],
                        });
                      }
                    }
                  }}
                  className={classNames(
                    showPopupPaneInputId === undefined && hideIfNotActive
                      ? "hidden"
                      : undefined,
                    "px-1 bg-canvas-bg text-neutral-600 transition touch-none hover:text-opacity-100",
                    isPaneFocused ? "bg-opacity-90" : "bg-opacity-70",
                    outputState !== "value" ? "italic" : undefined,
                    isPopupShown || selectionState !== undefined
                      ? "text-opacity-100"
                      : paneInput.name === undefined
                      ? "text-opacity-50"
                      : "text-opacity-50"
                  )}
                  onFocus={(e) => {
                    // prevent tooltip from showing when popover closes
                    e.preventDefault();
                  }}
                >
                  {paneInput.name ?? inputIndex}
                </button>
              </TooltipPrimitive.Trigger>
            </PopoverPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                side="top"
                sideOffset={4}
                align="center"
                className={TooltipClassName}
              >
                {tooltipContent ?? (
                  <>
                    <div>
                      <span className="font-semibold">click</span> to rename
                    </div>
                    <div>or remove input</div>
                  </>
                )}
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                side="left"
                sideOffset={6}
                align="start"
                alignOffset={-PANE_INPUT_POPUP_ALIGN_OFFSET}
                className="popover-scale-in"
                style={{
                  // @ts-ignore
                  "--radix-popper-transform-origin": `right ${PANE_INPUT_POPUP_ALIGN_OFFSET}x`,
                }}
              >
                <PaneInputPopup
                  paneId={paneId}
                  paneType={paneType}
                  paneInputs={paneInputs}
                  paneInput={paneInput}
                  inputIndex={inputIndex}
                  onRenameInput={onRenameInput}
                  onClose={() => {
                    setShowPopupPaneInputId(undefined);
                  }}
                />
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </PopoverPrimitive.Root>
        </TooltipPrimitive.Root>
      </div>
    );
  }
);

export const PaneInputNodes = observer(
  ({
    paneId,
    paneType,
    paneComputed,
    showPopupPaneInputId,
    setShowPopupPaneInputId,
    layoutId,
    onRenameInput,
  }: {
    paneId: string;
    paneType: PaneType;
    paneComputed: IComputedValue<Pane | undefined>;
    showPopupPaneInputId: PopupPaneInputId | undefined;
    setShowPopupPaneInputId: React.Dispatch<
      React.SetStateAction<PopupPaneInputId | undefined>
    >;
    layoutId: string | undefined;
    onRenameInput: (paneInputId: string, newInputName: string) => Promise<void>;
  }) => {
    const paneInputs = computed(() => {
      const pane = paneComputed.get();
      return pane !== undefined && "inputs" in pane ? pane.inputs : undefined;
    }).get();
    if (paneInputs === undefined) {
      return null;
    }
    return (
      <>
        {paneInputs.map((paneInput, inputIndex) => {
          return (
            <PaneInputNode
              inputIndex={inputIndex}
              paneId={paneId}
              paneType={paneType}
              paneInputs={paneInputs}
              paneInput={paneInput}
              showPopupPaneInputId={showPopupPaneInputId}
              setShowPopupPaneInputId={setShowPopupPaneInputId}
              hideIfNotActive={isCustomLayoutId(layoutId)}
              onRenameInput={onRenameInput}
              key={paneInput.id}
            />
          );
        })}
      </>
    );
  }
);

const NO_PANE_SENTINEL = "undefined";
const PaneInputSelector = observer(
  ({
    skipPaneId,
    value,
    onChange,
  }: {
    skipPaneId: string;
    value: string | undefined;
    onChange: (paneId: string | undefined) => void;
  }) => {
    const paneOptions = computed(
      () =>
        canvasAtom.value.panes
          .filter((p) => p.id !== skipPaneId)
          .map((p) => {
            return [
              p.id,
              p.name ??
                `[${PaneTypeNames[p.type]}] ${
                  p.type === PaneType.Evaluate
                    ? p.expression.substring(0, 16)
                    : p.type === PaneType.Import
                    ? `${p.module?.[1]}`
                    : PaneTypeNames[p.type]
                }`,
            ] as const;
          }),
      { equals: comparer.structural }
    ).get();
    return (
      <SelectRoot
        value={value ?? NO_PANE_SENTINEL}
        onChange={(v) => {
          onChange(v === NO_PANE_SENTINEL ? undefined : v);
        }}
        className="font-mono-natto w-full flex justify-between"
        contentClassName="font-mono-natto"
      >
        <SelectItem
          value={NO_PANE_SENTINEL}
          text="select a pane"
          className="text-opacity-50"
        />
        {paneOptions.map(([paneId, paneName]) => (
          <SelectItem value={paneId} text={paneName} key={paneId} />
        ))}
      </SelectRoot>
    );
  }
);

export const PaneRenderOutputConfigInput = observer(
  ({
    pane,
    paneInputSource,
    numPaneInputs,
    renderInputType,
    showPopupPaneInputId,
    setShowPopupPaneInputId,
    hideIfNotActive,
  }: {
    pane: Pane;
    paneInputSource: [paneId: string, outputIndex: number] | undefined;
    numPaneInputs: number;
    renderInputType: "custom" | "react-dom";
    showPopupPaneInputId: PopupPaneInputId | undefined;
    setShowPopupPaneInputId: React.Dispatch<
      React.SetStateAction<PopupPaneInputId | undefined>
    >;
    hideIfNotActive: boolean;
  }) => {
    const selectionState = useIsPaneInputSelected(
      pane.id,
      paneInputSource?.[0]
    );
    const isPopupShown =
      Array.isArray(showPopupPaneInputId) &&
      showPopupPaneInputId[0] === renderInputType;
    // hoverPaneInputId = false means this pane is not hovered
    // hoverPaneInputId = undefined means pane is hovered without input
    const hoverPaneInputId = computed(() => {
      const { value } = dragValueAtom;
      if (
        value?.type !== "reference" ||
        value.sourceInfo.type !== "pane-output" ||
        value.sourceInfo.hoverPaneId !== pane.id
      ) {
        return false;
      }
      return value.sourceInfo.hoverPaneInputKey;
    }).get();

    const bindDrag = usePaneInputBindDrag(
      paneInputSource,
      renderInputType === "custom"
        ? CUSTOM_RENDER_INPUT_LABEL
        : renderInputType === "react-dom"
        ? REACT_DOM_RENDER_INPUT_LABEL
        : undefined
    );

    return (
      <div
        className="pane-input absolute font-mono-natto mr-[3px]"
        style={{
          right: "100%",
          top: `${
            (numPaneInputs + (hoverPaneInputId === undefined ? 1 : 0)) *
              PANE_INPUT_HEIGHT +
            8
          }px`,
        }}
      >
        <PopoverPrimitive.Root
          open={isPopupShown}
          onOpenChange={(open) => {
            setShowPopupPaneInputId(open ? [renderInputType] : undefined);
          }}
          modal={true}
        >
          <PopoverPrimitive.Trigger asChild={true}>
            <button
              {...bindDrag()}
              onClick={(e) => {
                if (isModifierKeyDown(e.metaKey, e.ctrlKey)) {
                  e.preventDefault();
                  if (paneInputSource !== undefined) {
                    FocusPaneEmitter.emit("focus", {
                      paneId: paneInputSource[0],
                    });
                  }
                }
              }}
              className={classNames(
                showPopupPaneInputId === undefined && hideIfNotActive
                  ? "hidden"
                  : undefined,
                "px-1 text-primary-500 bg-neutral-200 bg-opacity-70 transition whitespace-nowrap hover:text-opacity-100 touch-none",
                isPopupShown
                  ? "text-opacity-100"
                  : selectionState !== undefined &&
                    !(
                      renderInputType === "react-dom" &&
                      paneInputSource === undefined &&
                      hoverPaneInputId === false
                    )
                  ? "text-opacity-100"
                  : "text-opacity-50"
              )}
            >
              {renderInputType === "custom"
                ? "render"
                : renderInputType === "react-dom"
                ? "ReactDOM"
                : ""}
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              side="left"
              sideOffset={6}
              className="popover-scale-in"
            >
              <div className="max-w-[16rem] bg-neutral-0 p-2 rounded-md shadow-lg select-none">
                <div className="font-mono-natto text-neutral-400 mb-2">
                  {renderInputType === "custom"
                    ? "custom renderer"
                    : renderInputType === "react-dom"
                    ? "ReactDOM"
                    : ""}
                </div>
                <div>
                  <PaneInputSelector
                    skipPaneId={pane.id}
                    value={paneInputSource?.[0]}
                    onChange={(selectedPaneId) => {
                      if (
                        selectedPaneId !== undefined &&
                        !checkAndConfirmPushPaneInput(
                          canvasAtom.value.panes,
                          pane.id,
                          selectedPaneId
                        )
                      ) {
                        return;
                      }
                      canvasDispatch([
                        ActionType.SetPaneRenderOutput,
                        {
                          paneId: pane.id,
                          renderOutput:
                            renderInputType === "custom"
                              ? selectedPaneId !== undefined
                                ? ["custom", [selectedPaneId, 0]]
                                : ["custom"]
                              : renderInputType === "react-dom"
                              ? selectedPaneId !== undefined
                                ? ["react", { ReactDOM: [selectedPaneId, 0] }]
                                : ["react"]
                              : undefined,
                        },
                      ]);
                    }}
                  />
                </div>
                {paneInputSource !== undefined ? (
                  <div className="mt-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowPopupPaneInputId(undefined);
                          // setTimeout to run after radix-ui popover focus management
                          setTimeout(() => {
                            FocusPaneEmitter.emit("focus", {
                              paneId: paneInputSource[0],
                            });
                          }, 50);
                        }}
                        className="flex items-center text-neutral-400 hover:text-neutral-600 focus:text-neutral-600 transition-colors"
                      >
                        <FeatherIcon
                          icon="arrow-left"
                          size="sm"
                          className="mr-1"
                        />
                        focus input pane
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={() => {
                          canvasDispatch([
                            ActionType.SetPaneRenderOutput,
                            {
                              paneId: pane.id,
                              renderOutput:
                                renderInputType === "custom"
                                  ? ["custom"]
                                  : renderInputType === "react-dom"
                                  ? ["react"]
                                  : undefined,
                            },
                          ]);
                          setShowPopupPaneInputId(undefined);
                        }}
                        className={removeButtonClassName}
                      >
                        <FeatherIcon
                          icon="scissors"
                          size="sm"
                          className="mr-1"
                        />
                        disconnect input
                      </button>
                    </div>
                  </div>
                ) : null}
                {renderInputType === "custom" ? (
                  <div className="mt-2">
                    <div className="text-neutral-600">
                      This virtual input is generated by the{" "}
                      <button
                        onClick={() => {
                          runInAction(() => {
                            showPaneSettingsPaneIdAtom.value = pane.id;
                          });
                          setShowPopupPaneInputId(undefined);
                        }}
                        className="text-primary-500 font-medium hover:text-primary-600 transition"
                      >
                        custom render output
                      </button>
                      .
                      {paneInputSource === undefined
                        ? " The value should have the following type."
                        : null}
                    </div>
                    {paneInputSource === undefined ? (
                      <code className="block text-neutral-400 whitespace-pre mt-4">
                        {`(element: HTMLElement) => {
  render: (value: any) => void,
  unmount?: () => void,
}`}
                      </code>
                    ) : null}
                  </div>
                ) : renderInputType === "react-dom" ? (
                  <div className="mt-2">
                    <div className="text-neutral-600">
                      This virtual input is generated by the{" "}
                      <button
                        onClick={() => {
                          runInAction(() => {
                            showPaneSettingsPaneIdAtom.value = pane.id;
                          });
                          setShowPopupPaneInputId(undefined);
                        }}
                        className="text-primary-500 font-medium hover:text-primary-600 transition"
                      >
                        react render output
                      </button>
                      . Optionally connect a{" "}
                      <span className="font-mono-natto">ReactDOM</span> to
                      render with.
                    </div>
                  </div>
                ) : null}
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </div>
    );
  }
);

export const PaneRenderOutputConfigInputs = observer(
  ({
    paneComputed,
    showPopupPaneInputId,
    setShowPopupPaneInputId,
    layoutId,
  }: {
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
    showPopupPaneInputId: PopupPaneInputId | undefined;
    setShowPopupPaneInputId: React.Dispatch<
      React.SetStateAction<PopupPaneInputId | undefined>
    >;
    layoutId: string | undefined;
  }) => {
    const pane = paneComputed.get();
    return (
      <>
        {pane?.type === PaneType.Evaluate &&
        pane.renderOutput?.[0] === "custom" ? (
          <PaneRenderOutputConfigInput
            pane={pane}
            paneInputSource={pane.renderOutput[1]}
            numPaneInputs={pane.inputs.length}
            renderInputType="custom"
            showPopupPaneInputId={showPopupPaneInputId}
            setShowPopupPaneInputId={setShowPopupPaneInputId}
            hideIfNotActive={isCustomLayoutId(layoutId)}
          />
        ) : null}
        {pane?.type === PaneType.Evaluate &&
        pane.renderOutput?.[0] === "react" ? (
          <PaneRenderOutputConfigInput
            pane={pane}
            paneInputSource={pane.renderOutput[1]?.ReactDOM}
            numPaneInputs={pane.inputs.length}
            renderInputType="react-dom"
            showPopupPaneInputId={showPopupPaneInputId}
            setShowPopupPaneInputId={setShowPopupPaneInputId}
            hideIfNotActive={isCustomLayoutId(layoutId)}
          />
        ) : null}
      </>
    );
  }
);

const PaneInputWire = observer(
  ({
    paneId,
    paneType,
    paneInputId,
    paneInputName,
    inputIndex,
    inputPaneId,
    inputPaneOutputIndex,
    className,
  }: {
    paneId: string;
    paneType: PaneType;
    paneInputId: EvalPaneInputId;
    paneInputName: string | undefined;
    inputIndex: number;
    inputPaneId: string;
    inputPaneOutputIndex: number;
    className?: string;
  }) => {
    const [contextMenuPosition, setContextMenuPosition] = useState<
      Position | undefined
    >();
    if (
      contextMenuPosition !== undefined &&
      canvasLayoutIdAtom.value !== undefined
    ) {
      setContextMenuPosition(undefined);
    }
    const inputPaneState = activePaneStates.get(inputPaneId);
    if (inputPaneState === undefined) {
      return null;
    }
    const wireFrom = computed((): Position => {
      const transferInputSource = transferPaneInputSourceBox.get();
      if (
        transferInputSource !== undefined &&
        transferInputSource.paneInputs.some(
          ([_transferPaneType, transferPaneId, transferPaneInputId]) =>
            paneId === transferPaneId &&
            areEvalPaneInputIdsEqual(paneInputId, transferPaneInputId)
        )
      ) {
        if (transferInputSource.hoverPaneOutput !== undefined) {
          const hoverPaneState = activePaneStates.get(
            transferInputSource.hoverPaneOutput[0]
          );
          if (hoverPaneState !== undefined) {
            return getPaneOutputWirePosition(hoverPaneState.rect.get(), 0);
          }
        }
        return transferInputSource.position;
      }
      return getPaneOutputWirePosition(
        inputPaneState.rect.get(),
        inputPaneOutputIndex
      );
    }).get();
    const activePaneState = activePaneStates.get(paneId)!;
    const [x2, y2] = activePaneState.rect.get();
    const isAnyPaneFocused = computed(
      () => focusedPaneIdAtom.value !== undefined
    ).get();
    const isSelected =
      inputPaneId !== undefined &&
      computed(
        () =>
          selectedPaneIdsAtom.value.includes(paneId) ||
          selectedPaneIdsAtom.value.includes(inputPaneId)
      ).get();
    const isFocused = computed(
      () =>
        focusedPaneIdAtom.value === paneId ||
        (inputPaneId !== undefined && focusedPaneIdAtom.value === inputPaneId)
    ).get();
    const isRenderingInputNodes =
      activePaneState.viewMode.get() === PaneViewMode.Normal;
    return (
      <>
        <Wire
          from={wireFrom}
          to={[
            x2 -
              (isRenderingInputNodes
                ? 3 +
                  8 +
                  getWidthForMonospaceText(paneInputName ?? `${inputIndex}`)
                : 0),
            y2 + 16.5 + (isRenderingInputNodes ? inputIndex : 0) * 16,
          ]}
          opacityClassName={
            isFocused || contextMenuPosition !== undefined
              ? "text-opacity-30"
              : isSelected
              ? "text-opacity-20"
              : isAnyPaneFocused
              ? "text-opacity-10"
              : "text-opacity-[0.15]"
          }
          onClick={(e: React.MouseEvent) => {
            setContextMenuPosition([e.clientX, e.clientY]);
            e.preventDefault();
          }}
          onContextMenu={(e: React.MouseEvent) => {
            setContextMenuPosition([e.clientX, e.clientY]);
            e.preventDefault();
          }}
          className={classNames(
            contextMenuPosition !== undefined || isFocused || isSelected
              ? "z-99998"
              : "z-0",
            className
          )}
        />
        {contextMenuPosition !== undefined ? (
          <PopperLayer
            reference={{
              current: {
                getBoundingClientRect: (): any => ({
                  width: 0,
                  height: 0,
                  top: contextMenuPosition[1],
                  right: contextMenuPosition[0],
                  bottom: contextMenuPosition[1],
                  left: contextMenuPosition[0],
                }),
              },
            }}
            render={({ state, completeTransitionExit }) => (
              <AnimatedPopout
                state={state}
                completeTransitionExit={completeTransitionExit}
                transformOrigin="top left"
              >
                <PaneWireContextMenu
                  paneId={paneId}
                  paneType={paneType}
                  paneInputId={paneInputId}
                  onClose={() => setContextMenuPosition(undefined)}
                />
              </AnimatedPopout>
            )}
            options={{ placement: "bottom-start" }}
            onOutsideMouseDown={() => setContextMenuPosition(undefined)}
            transitionExit={true}
          />
        ) : null}
      </>
    );
  }
);

export const PaneInputWires = observer(
  ({
    paneId,
    paneType,
    paneInputsComputed,
    paneComputed,
  }: {
    paneId: string;
    paneType: PaneType;
    paneInputsComputed: IComputedValue<PaneInput[] | undefined>;
    paneComputed: IComputedValue<Pane | undefined>;
  }) => {
    const paneInputs = paneInputsComputed.get();
    const renderOutputCustomInput = computed(() => {
      if (paneType !== PaneType.Evaluate) {
        return undefined;
      }
      const pane = paneComputed.get()!;
      return "renderOutput" in pane && pane.renderOutput?.[0] === "custom"
        ? pane.renderOutput[1]
        : undefined;
    }).get();
    const renderOutputReactDOMInput = computed(() => {
      if (paneType !== PaneType.Evaluate) {
        return undefined;
      }
      const pane = paneComputed.get()!;
      return "renderOutput" in pane && pane.renderOutput?.[0] === "react"
        ? pane.renderOutput[1]?.ReactDOM
        : undefined;
    }).get();

    function renderInputWire(
      inputPaneId: string | undefined,
      paneInputId: string | ["render", "custom" | "react-dom"],
      paneInputName: string | undefined,
      paneInputSource: [paneId: string, outputIndex: number] | undefined,
      inputIndex: number
    ) {
      if (inputPaneId === undefined || paneInputSource === undefined) {
        return null;
      }
      let addOneForRenderInput = false;
      if (Array.isArray(paneInputId)) {
        addOneForRenderInput =
          dragValueAtom.value?.type === "reference" &&
          dragValueAtom.value.sourceInfo.type === "pane-output" &&
          dragValueAtom.value.sourceInfo.hoverPaneId === paneId &&
          dragValueAtom.value.sourceInfo.hoverPaneInputKey === undefined;
      }
      return (
        <PaneInputWire
          paneId={paneId}
          paneType={paneType}
          paneInputId={paneInputId}
          paneInputName={paneInputName}
          inputIndex={inputIndex + (addOneForRenderInput ? 1 : 0)}
          inputPaneId={inputPaneId}
          inputPaneOutputIndex={paneInputSource[1]}
          key={Array.isArray(paneInputId) ? paneInputId[0] : paneInputId}
        />
      );
    }

    return (
      <>
        {paneInputs !== undefined
          ? paneInputs.map((paneInput, i) =>
              renderInputWire(
                getPaneIdFromPaneInput(paneInput),
                paneInput.id,
                paneInput.name,
                paneInput.source,
                i
              )
            )
          : null}
        {renderOutputCustomInput !== undefined
          ? renderInputWire(
              renderOutputCustomInput[0],
              ["render", "custom"],
              CUSTOM_RENDER_INPUT_LABEL,
              renderOutputCustomInput,
              paneInputs!.length
            )
          : null}
        {renderOutputReactDOMInput !== undefined
          ? renderInputWire(
              renderOutputReactDOMInput[0],
              ["render", "react-dom"],
              REACT_DOM_RENDER_INPUT_LABEL,
              renderOutputReactDOMInput,
              paneInputs!.length
            )
          : null}
      </>
    );
  }
);

export const DragPaneOutputWireImpl = observer(
  ({
    fromPaneId,
    fromPaneOutputIndex,
    duplicateInputName,
    toPaneId,
    toPaneInputId,
    mousePosition,
  }: {
    fromPaneId: string;
    fromPaneOutputIndex: number | undefined;
    duplicateInputName: string | undefined;
    toPaneId: string | undefined;
    toPaneInputId: EvalPaneInputId | undefined;
    mousePosition: Position;
  }) => {
    const [x1, y1, w1] = activePaneStates.get(fromPaneId)!.rect.get();
    const toPaneState =
      toPaneId !== undefined ? activePaneStates.get(toPaneId) : undefined;
    const [x2, y2] = toPaneState?.rect.get() ?? mousePosition;
    const toPane =
      toPaneId !== undefined
        ? canvasAtom.value.panes.find((p) => p.id === toPaneId)
        : undefined;
    let toPaneInputs =
      toPane !== undefined && "inputs" in toPane
        ? // This is not responsive but should be okay for duration of drag
          toPane.inputs
        : undefined;
    if (toPaneId !== undefined && toPaneInputs === undefined) {
      if (toPane?.type === PaneType.EvaluateTemplate) {
        const activePaneState = activePaneStates.get(toPaneId)!;
        assert(activePaneState.type === PaneType.EvaluateTemplate);
        toPaneInputs = activePaneState.paneInputs.value;
      } else if (toPane?.type === PaneType.LLM) {
        const activePaneState = activePaneStates.get(toPaneId)!;
        assert(activePaneState.type === PaneType.LLM);
        toPaneInputs = activePaneState.paneInputsComputed.get();
      }
    }
    const existingPaneInputName: string | undefined =
      toPaneInputs !== undefined && toPaneInputId !== undefined
        ? toPaneInputs.find((paneInput) => paneInput.id === toPaneInputId)?.name
        : undefined;
    let inputNameElement;
    let toX = x2 + 1;
    let toPaneInputIndex = undefined;
    let inputName: string | undefined;
    if (toPaneState?.viewMode.get() === PaneViewMode.Normal) {
      if (Array.isArray(toPaneInputId)) {
        assert(toPaneInputId[0] === "render");
        toPaneInputIndex =
          toPane !== undefined && "inputs" in toPane ? toPane.inputs.length : 0;
        inputName =
          toPaneInputId[1] === "custom"
            ? CUSTOM_RENDER_INPUT_LABEL
            : toPaneInputId[1] === "react-dom"
            ? REACT_DOM_RENDER_INPUT_LABEL
            : undefined;
      } else if (toPaneInputs !== undefined) {
        toPaneInputIndex =
          toPaneInputId !== undefined
            ? toPaneInputs.findIndex(
                (paneInput) => paneInput.id === toPaneInputId
              )
            : toPaneInputs.length;
        const fromPane = canvasAtom.value.panes.find(
          (p) => p.id === fromPaneId
        );
        inputName = `${
          existingPaneInputName ??
          (duplicateInputName !== undefined
            ? validatePaneInputName(
                duplicateInputName,
                toPaneInputs,
                toPaneInputId
              )
            : getPaneInputAutoName(
                toPaneInputs,
                fromPaneOutputIndex,
                fromPane,
                toPaneInputId
              )) ??
          toPaneInputIndex ??
          toPaneInputs.length
        }`;
        inputNameElement = (
          <div
            className="pane-input absolute text-neutral-700 z-99998"
            style={{
              left: `${x2 - 3}px`,
              top: `${
                y2 + (toPaneInputIndex ?? toPaneInputs.length) * 16 + 8
              }px`,
            }}
          >
            <div className="absolute top-0 right-0 px-1 bg-neutral-200 font-mono-natto">
              {inputName}
            </div>
          </div>
        );
      }
    } else {
      toPaneInputIndex = 0;
    }
    if (inputName !== undefined) {
      toX = x2 - 3 - 8 - getWidthForMonospaceText(inputName);
    }
    return (
      <>
        <Wire
          from={[x1 + w1 + 1, y1 + 16.5 + (fromPaneOutputIndex ?? 0) * 16]}
          to={[
            toX,
            y2 + 16.5 + (toPaneInputIndex ?? toPaneInputs?.length ?? 0) * 16,
          ]}
          opacityClassName="opacity-50"
          disable={true}
          className="z-99999"
        />
        {inputNameElement}
      </>
    );
  }
);
